import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { Btn, DateInp, BottomSheet } from "./ui.jsx";
import { FONT, RADIUS, SPRING } from "../lib/theme.js";
import { uid, MONTH_NAMES, currSym } from "../lib/calculations.js";

// ─── Partner / category definitions ──────────────────────────────────────────
const PARTNER_CATS = [
  { value:"employee", label:"VA / Employee",        icon:"👤" },
  { value:"software", label:"Software / SaaS",      icon:"💻" },
  { value:"agency",   label:"Agency",               icon:"📣" },
  { value:"prep",     label:"Prep Center",          icon:"🏭" },
  { value:"custom",   label:"Custom",               icon:"📌" },
];

const TX_CATS = [
  { value:"general", label:"General",           icon:"📋",
    tip:"A standard income or expense. Appears in your monthly cashflow exactly as entered. Example: office rent, ad spend, one-time sale." },
  { value:"partner", label:"Partner / Service", icon:"🤝",
    tip:"Payments to VAs, agencies, software tools, or prep centers. Tracked separately so you can analyse people and service costs. Can be one-time or recurring." },
  { value:"credit",  label:"Credit Line / Loan",icon:"🏦",
    tip:"Borrowed funds or loan repayments. Set a growth rate below to model compound interest automatically. Example: Business loan drawdown at 5% monthly interest." },
];

const TIPS = {
  category:    "Choose the type of transaction. This affects how it is categorised in your reports and which extra fields are shown.",
  name:        "A clear label — used in your monthly view, reports, and Planner. Example: 'Office rent', 'VA — Maria', 'Business loan drawdown'.",
  amount:      "The amount per occurrence. For recurring items this is the amount per payment cycle. For credit lines with a growth rate, this is the starting amount before interest is applied.",
  date:        "The date this one-time transaction occurs. It will appear in the month containing this date. Leave blank to add it to the currently selected month.",
  startDate:   "The first date this recurring payment begins. Entries are only generated from this month onwards. Example: hire a VA in March — set 01.03.2026 and no charges appear in Jan or Feb.",
  endDate:     "Optional. The last month this payment should appear. After this date no more entries are generated. Useful for fixed-term loans, seasonal costs, or temporary contracts.",
  dayOfMonth:  "Which day of each month this payment is due. Day 1 = first of month. Day 31 = last day (auto-adjusted for shorter months). Affects when it shows as due in your cashflow.",
  activeMonths:"Which months this payment applies to. Deselect months to skip them — for example an agency you only pay Oct–Dec. All months = year-round.",
  activeYears: "Which years this applies to. Remove future years to automatically stop this payment in those years.",
  intervalDays:"Days between each payment. 7 = weekly, 14 = fortnightly, 30 = monthly, 90 = quarterly, 365 = annual. First payment starts on your start date.",
  growthRate:  "Percentage increase each time the growth trigger fires. Example: 10% means the amount grows by 10% each cycle. Use for loans with compound interest, escalating rent, or tiered fees.",
  growthEvery: "How many transactions before the growth rate applies. 1 = grows every payment. 3 = grows every 3rd payment. Example: loan that increases 5% every 3 months: rate=5, every=3.",
  recurring:   "Recurring transactions appear automatically in every qualifying month. One-time transactions appear only in the month of their date. Use recurring for salaries, subscriptions, and loan repayments.",
  planner:     "Creates a linked decision on your Planner timeline. Lets you see the strategic impact of this payment alongside other business decisions — useful for tracking when you hired someone or took a loan.",
  plannerCat:  "How this decision is categorised in Planner. Cost = ongoing expense. Investment = expected to generate return. Milestone = key business event.",
};

// ─── Viewport-aware tooltip ───────────────────────────────────────────────────
function Tip({ text }) {
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const [below, setBelow] = useState(false);
  const [mobile, setMobile] = useState(false);
  const ref = useRef(null);

  const toggle = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const vh = window.innerHeight;
      setBelow(rect.top < vh * 0.45); // if in top half show below, else above
      setMobile(window.innerWidth < 640);
    }
    setOpen(o => !o);
  }, []);

  useEffect(() => {
    if (!open) return;
    function close(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [open]);

  return (
    <span ref={ref} style={{ position:"relative", display:"inline-flex",
      alignItems:"center", marginLeft:4, verticalAlign:"middle" }}>
      <button onClick={toggle} type="button"
        style={{ display:"inline-flex", alignItems:"center", justifyContent:"center",
          width:16, height:16, borderRadius:"50%",
          background: open ? T.accent+"30" : T.surfaceHi,
          color: open ? T.accent : T.textSub,
          fontSize:9, fontWeight:700, cursor:"pointer",
          border:`1px solid ${open ? T.accent+"60" : T.border}`,
          fontFamily:FONT, flexShrink:0, padding:0,
          transition:"all 0.15s" }}>ⓘ</button>

      {open && (
        mobile ? (
          // Mobile: full-width banner below label
          <div style={{
            position:"fixed", left:12, right:12, zIndex:99999,
            top: "50%", transform:"translateY(-50%)",
            background:T.cardHi, border:`1px solid ${T.accent+"40"}`,
            borderRadius:RADIUS.lg, padding:"14px 16px",
            fontSize:13, color:T.text, lineHeight:1.65,
            boxShadow:`0 16px 48px rgba(0,0,0,0.6)`,
          }}>
            <div style={{ display:"flex", justifyContent:"space-between",
              alignItems:"flex-start", gap:8, marginBottom:6 }}>
              <span style={{ fontWeight:700, color:T.accent, fontSize:11,
                textTransform:"uppercase", letterSpacing:0.5 }}>Info</span>
              <button onClick={()=>setOpen(false)} type="button"
                style={{ background:"none", border:"none", color:T.textSub,
                  cursor:"pointer", fontSize:16, lineHeight:1, padding:0 }}>✕</button>
            </div>
            {text}
          </div>
        ) : (
          // Desktop: smart bubble above or below
          <div style={{
            position:"absolute",
            left:"50%", transform:"translateX(-50%)",
            ...(below
              ? { top:"calc(100% + 8px)" }
              : { bottom:"calc(100% + 8px)" }),
            zIndex:99999,
            background:T.cardHi, border:`1px solid ${T.accent+"30"}`,
            borderRadius:RADIUS.md, padding:"10px 14px",
            fontSize:12, color:T.text, lineHeight:1.65,
            width:`min(280px, calc(100vw - 40px))`,
            whiteSpace:"normal", wordBreak:"break-word",
            boxShadow:`0 12px 40px rgba(0,0,0,0.5)`,
            pointerEvents:"none",
          }}>
            {/* Arrow */}
            <div style={{
              position:"absolute", left:"50%",
              ...(below ? { top:-5 } : { bottom:-5 }),
              width:8, height:8, background:T.cardHi,
              border:`1px solid ${T.accent+"30"}`,
              borderRight:"none", borderBottom:"none",
              transform:`translateX(-50%) rotate(${below?"45deg":"225deg"})`,
            }}/>
            {text}
          </div>
        )
      )}
    </span>
  );
}

// ─── Field label row ──────────────────────────────────────────────────────────
function FL({ children, tip, color }) {
  const T = useTheme();
  return (
    <div style={{ display:"flex", alignItems:"center", marginBottom:7 }}>
      <span style={{ fontSize:11, fontWeight:700,
        color: color || T.text,
        textTransform:"uppercase", letterSpacing:0.6,
        fontFamily:FONT }}>{children}</span>
      {tip && <Tip text={tip}/>}
    </div>
  );
}

// ─── Styled input (iOS-safe 16px min font) ────────────────────────────────────
function TxInput({ value, onChange, placeholder, type="text", color, style={} }) {
  const T = useTheme();
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onFocus={()=>setFocused(true)}
      onBlur={()=>setFocused(false)}
      style={{
        width:"100%", boxSizing:"border-box",
        background:T.surfaceHi,
        border:`1.5px solid ${focused ? (color||T.accent) : T.borderHi}`,
        borderRadius:RADIUS.md,
        color: color || T.text,
        padding:"10px 14px",
        fontSize:16, // 16px minimum prevents iOS Safari zoom
        fontFamily:FONT,
        outline:"none",
        transition:`border-color 0.15s`,
        ...style,
      }}
    />
  );
}

// ─── Segmented control ────────────────────────────────────────────────────────
function Seg({ options, value, onChange, color }) {
  const T = useTheme();
  return (
    <div style={{ display:"flex", background:T.surface,
      borderRadius:RADIUS.md, padding:3, gap:2 }}>
      {options.map(([v,l]) => (
        <button key={v} type="button" onClick={()=>onChange(v)} style={{
          flex:1, padding:"8px 4px", borderRadius:RADIUS.sm,
          fontFamily:FONT, fontSize:13, fontWeight:value===v?700:400,
          cursor:"pointer", border:"none",
          background:value===v?(color||T.accent)+"22":"transparent",
          color:value===v?(color||T.accent):T.textSub,
          transition:"all 0.15s",
        }}>{l}</button>
      ))}
    </div>
  );
}

// ─── Info banner ──────────────────────────────────────────────────────────────
function InfoBanner({ color, icon, children }) {
  const T = useTheme();
  return (
    <div style={{
      background:(color||T.blue)+"15",
      border:`1px solid ${(color||T.blue)}35`,
      borderRadius:RADIUS.md, padding:"10px 14px",
      fontSize:12, color:T.text, lineHeight:1.65,
      display:"flex", gap:8, alignItems:"flex-start",
    }}>
      <span style={{ fontSize:14, flexShrink:0 }}>{icon||"ℹ"}</span>
      <div>{children}</div>
    </div>
  );
}

// ─── Month pill selector — compact on mobile ──────────────────────────────────
function MonthPicker({ value, onChange, color }) {
  const T = useTheme();
  const allMonths = [1,2,3,4,5,6,7,8,9,10,11,12];
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;

  return (
    <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
      {/* Quick select buttons */}
      <div style={{ width:"100%", display:"flex", gap:6, marginBottom:6 }}>
        <button type="button" onClick={()=>onChange([...allMonths])}
          style={{ fontSize:11, color:color||T.accent, background:"none",
            border:`1px solid ${(color||T.accent)+"40"}`,
            borderRadius:RADIUS.pill, padding:"2px 10px",
            cursor:"pointer", fontFamily:FONT }}>All</button>
        <button type="button" onClick={()=>onChange([])}
          style={{ fontSize:11, color:T.textSub, background:"none",
            border:`1px solid ${T.border}`,
            borderRadius:RADIUS.pill, padding:"2px 10px",
            cursor:"pointer", fontFamily:FONT }}>None</button>
        <button type="button"
          onClick={()=>onChange([1,2,3,4,5,6,7,8,9,10,11,12].filter(m=>[10,11,12].includes(m)))}
          style={{ fontSize:11, color:T.textSub, background:"none",
            border:`1px solid ${T.border}`,
            borderRadius:RADIUS.pill, padding:"2px 10px",
            cursor:"pointer", fontFamily:FONT }}>Q4</button>
      </div>
      {allMonths.map(m => {
        const sel = value.includes(m);
        return (
          <button key={m} type="button" onClick={()=>{
            onChange(sel ? value.filter(x=>x!==m) : [...value,m].sort((a,b)=>a-b));
          }} style={{
            padding:"3px 7px",
            borderRadius:RADIUS.pill,
            background:sel?(color||T.blue)+"22":"transparent",
            color:sel?(color||T.blue):T.textSub,
            border:`1px solid ${sel?(color||T.blue):T.border}`,
            fontSize:11, fontFamily:FONT, cursor:"pointer",
            fontWeight:sel?700:400,
            minWidth:32, textAlign:"center",
          }}>{MONTH_NAMES[m-1].slice(0,3)}</button>
        );
      })}
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export default function AddTransactionModal({
  open, onClose,
  defaultType = "expense",
  defaultCategory = "general",
  selectedMonth,
  visibleYears,
  recurringIncomeOps,
  recurringExpenseOps,
  setPartners,
  partners,
  monthOps,
  onAddDecision,
  // Fires after a successful submit with the id of the new transaction.
  // Lets the caller scroll + red-pulse the newly created row.
  onCreated,
}) {
  const T = useTheme();
  const allMonths = [1,2,3,4,5,6,7,8,9,10,11,12];
  const step2Ref = useRef(null);
  const contentRef = useRef(null);

  // ── Step 1 state ──────────────────────────────────────────────────────────
  const [txType,    setTxType]    = useState(defaultType);
  const [category,  setCategory]  = useState(defaultCategory);
  const [name,      setName]      = useState("");
  const [amount,    setAmount]    = useState("");

  // ── Step 2 state ──────────────────────────────────────────────────────────
  const [isRecurring, setIsRecurring] = useState(false);
  const [date,        setDate]        = useState("");
  const [startDate,   setStartDate]   = useState("");
  const [endDate,     setEndDate]     = useState("");
  const [recType,     setRecType]     = useState("calendar");
  const [dayOfMonth,  setDayOfMonth]  = useState(1);
  const [activeMonths,setActiveMonths]= useState([...allMonths]);
  const [activeYears, setActiveYears] = useState(visibleYears ? [...visibleYears] : []);
  const [intervalDays,setIntervalDays]= useState(30);
  const [growthRate,  setGrowthRate]  = useState("");
  const [growthEvery, setGrowthEvery] = useState(1);
  const [partnerCat,  setPartnerCat]  = useState("employee");
  const [addPlanner,  setAddPlanner]  = useState(false);
  const [plannerCat,  setPlannerCat]  = useState("cost");

  // Step 2 is visible when name AND amount are filled
  const step2Visible = name.trim().length > 0 && parseFloat(amount) > 0;

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setTxType(defaultType); setCategory(defaultCategory);
    setName(""); setAmount("");
    setIsRecurring(false); setDate(""); setStartDate(""); setEndDate("");
    setRecType("calendar"); setDayOfMonth(1);
    setActiveMonths([...allMonths]);
    setActiveYears(visibleYears ? [...visibleYears] : []);
    setIntervalDays(30); setGrowthRate(""); setGrowthEvery(1);
    setPartnerCat("employee");
    setAddPlanner(false); setPlannerCat("cost");
  }, [open]);

  // Auto-scroll to step 2 when it appears
  useEffect(() => {
    if (step2Visible && step2Ref.current && contentRef.current) {
      setTimeout(() => {
        step2Ref.current?.scrollIntoView({ behavior:"smooth", block:"nearest" });
      }, 80);
    }
  }, [step2Visible]);

  // ── Body class — must be before any early returns (Rules of Hooks) ───────────
  useEffect(() => {
    if (!open) {
      document.body.classList.remove("sf-overlay-open");
      return;
    }
    document.body.classList.add("sf-overlay-open");
    return () => document.body.classList.remove("sf-overlay-open");
  }, [open]);

  if (!open) return null;

  const isIncome  = txType === "income";
  const isPartner = category === "partner";
  const isCredit  = category === "credit";
  const mainColor = isIncome ? T.green : isCredit ? T.orange : isPartner ? T.purple : T.red;

  const mo = selectedMonth ? parseInt(selectedMonth.split("-")[1]) : new Date().getMonth()+1;
  const yr = selectedMonth ? parseInt(selectedMonth.split("-")[0]) : new Date().getFullYear();
  const monthName = MONTH_NAMES[mo-1];

  function submit() {
    if (!name.trim() || !amount) return;
    const amt = parseFloat(amount) || 0;
    const id  = uid();

    if (isRecurring) {
      const base = {
        id, name:name.trim(), amount:amt,
        type:recType, isIncome,
        startDate, endDate,
        dayOfMonth, activeMonths, activeYears,
        intervalDays,
        growthRate: growthRate ? parseFloat(growthRate) : 0,
        growthEvery: parseInt(growthEvery) || 1,
        category: isPartner ? partnerCat : isCredit ? "credit" : "general",
      };
      if (isPartner || isCredit) {
        setPartners?.([{ ...base, isIncome }, ...(partners||[])]);
      } else if (isIncome) {
        recurringIncomeOps?.add(base);
      } else {
        recurringExpenseOps?.add(base);
      }
    } else {
      if (isIncome) {
        monthOps?.addIncomeItem?.(selectedMonth, { id, what:name.trim(), amount:amt, date, isManual:true });
      } else {
        monthOps?.addExpense?.(selectedMonth, { id, what:name.trim(), amount:amt, date, paid:false });
      }
    }

    if (addPlanner) {
      const iconMap = { general:isIncome?"💰":"💸", partner:"🤝", credit:"🏦" };
      onAddDecision?.({
        id:uid(), name:name.trim(),
        icon:iconMap[category]||"📌",
        category:plannerCat,
        date:{ prec:"month", month:selectedMonth },
        status:"planned",
        notes:`${isRecurring?"Recurring":"One-time"} ${isIncome?"income":"expense"} · ${currSym()}${amt}${isRecurring?"/period":""}`,
        color:mainColor, recurring:isRecurring, amount:amt,
      });
    }
    // Notify caller so they can scroll/highlight the new row. Pass the
    // synthesized id regardless of which storage bucket the transaction
    // landed in — recurring lists, partners, and per-month items all use
    // the same `id` field for the row's DOM anchor.
    onCreated?.(id);
    onClose();
  }

  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;


  const footerNode = (
    <>
      <Btn onClick={onClose} variant="outline" color={T.textSub} style={{ flex:1 }}>Cancel</Btn>
      <Btn
        onClick={submit}
        color={mainColor}
        disabled={!name.trim() || !amount || parseFloat(amount)<=0}
        style={{ flex:2 }}
      >
        {isRecurring ? "Add Recurring" : "Add"}{" "}
        {isIncome ? "Income" : "Expense"}
        {addPlanner ? " + Planner" : ""}
      </Btn>
    </>
  );

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="+ Add Transaction"
      subtitle={`${monthName} ${yr}`}
      height="88vh"
      footer={footerNode}
    >
      {/* ── Scrollable content ── */}
      <div ref={contentRef} style={{ display:"flex", flexDirection:"column", gap:18 }}>

          {/* ══ STEP 1 ══════════════════════════════════════════════════════ */}

          {/* Income / Expense */}
          <div style={{ display:"flex", gap:8 }}>
            {[
              {id:"expense", label:"💸 Expense", color:T.red},
              {id:"income",  label:"💰 Income",  color:T.green},
            ].map(t => (
              <button key={t.id} type="button" onClick={()=>setTxType(t.id)} style={{
                flex:1, padding:"11px 0",
                borderRadius:RADIUS.md, fontFamily:FONT, fontSize:14,
                fontWeight:txType===t.id?700:500, cursor:"pointer",
                background:txType===t.id?t.color+"25":"transparent",
                color:txType===t.id?t.color:T.textSub,
                border:`2px solid ${txType===t.id?t.color:T.border}`,
                transition:"all 0.18s",
              }}>{t.label}</button>
            ))}
          </div>

          {txType === "income" && !isRecurring && (
            <div style={{ fontSize:11, color:T.orange, padding:"8px 12px", background:T.orange+"08", borderRadius:RADIUS.sm, border:`1px solid ${T.orange}20`, lineHeight:1.5 }}>
              This month already has projected income from your marketplace targets. Manual income will be added on top — use this for genuine extra income (refunds, settlements, etc.), not to duplicate projected sales.
            </div>
          )}

          {/* Category */}
          <div>
            <FL tip={TIPS.category}>Category</FL>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {TX_CATS.map(c => (
                <button key={c.value} type="button" onClick={()=>setCategory(c.value)} style={{
                  display:"flex", alignItems:"center", gap:5,
                  padding:"8px 14px", borderRadius:RADIUS.md, fontFamily:FONT,
                  fontSize:13, cursor:"pointer", fontWeight:category===c.value?700:400,
                  background:category===c.value?mainColor+"20":"transparent",
                  color:category===c.value?mainColor:T.textSub,
                  border:`1.5px solid ${category===c.value?mainColor:T.border}`,
                  transition:"all 0.15s", flex:1, justifyContent:"center",
                }}>
                  <span>{c.icon}</span>
                  <span>{c.label}</span>
                  <Tip text={c.tip}/>
                </button>
              ))}
            </div>
          </div>

          {/* Partner subcategory */}
          {isPartner && (
            <div>
              <FL>Partner Type</FL>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                {PARTNER_CATS.map(c => (
                  <button key={c.value} type="button" onClick={()=>setPartnerCat(c.value)} style={{
                    padding:"6px 12px", borderRadius:RADIUS.pill, fontFamily:FONT,
                    fontSize:12, cursor:"pointer", fontWeight:partnerCat===c.value?700:400,
                    background:partnerCat===c.value?T.purple+"22":"transparent",
                    color:partnerCat===c.value?T.purple:T.textSub,
                    border:`1px solid ${partnerCat===c.value?T.purple:T.border}`,
                    transition:"all 0.15s",
                  }}>{c.icon} {c.label}</button>
                ))}
              </div>
            </div>
          )}

          {/* Credit line info */}
          {isCredit && (
            <InfoBanner color={T.orange} icon="🏦">
              Set the amount received or repaid per cycle. Use <strong>Growth Rate</strong> in the schedule section to model compound interest. Example: a {currSym()}10,000 loan at 5% monthly interest — amount {currSym()}10,000, growth rate 5%, every 1 transaction.
            </InfoBanner>
          )}

          {/* Name */}
          <div>
            <FL tip={TIPS.name}>Name / Description</FL>
            <TxInput
              value={name} onChange={e=>setName(e.target.value)}
              color={mainColor}
              placeholder={
                isPartner ? "e.g. VA — Maria, Agency — ClickBoost" :
                isCredit  ? "e.g. Business loan, Bank overdraft" :
                isIncome  ? "e.g. Marketplace payout, Bonus payment" :
                            "e.g. Office rent, Packaging materials"
              }
            />
          </div>

          {/* Amount */}
          <div>
            <FL tip={TIPS.amount}>Amount ({currSym()})</FL>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:20, fontWeight:700, color:T.textSub }}>{currSym()}</span>
              <TxInput
                type="number" value={amount}
                onChange={e=>setAmount(e.target.value)}
                placeholder="0.00" color={mainColor}
                style={{ fontSize:20, fontWeight:700 }}
              />
            </div>
          </div>

          {/* ══ STEP 2 — appears when Step 1 is filled ══════════════════════ */}
          <div ref={step2Ref} style={{
            maxHeight: step2Visible ? "2000px" : "0",
            overflow:"hidden",
            transition:`max-height 0.45s ${SPRING}`,
          }}>
            <div style={{ display:"flex", flexDirection:"column", gap:16,
              paddingTop:4,
              borderTop:`1px solid ${T.border}`,
              marginTop:4,
            }}>

              {/* Divider label */}
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ flex:1, height:1, background:T.border }}/>
                <span style={{ fontSize:11, color:T.textSub, fontWeight:600,
                  textTransform:"uppercase", letterSpacing:0.5 }}>Schedule</span>
                <div style={{ flex:1, height:1, background:T.border }}/>
              </div>

              {/* One-time vs Recurring */}
              <div>
                <FL tip={TIPS.recurring}>Transaction Type</FL>
                <Seg
                  options={[["once","📅 One-time"],["recurring","🔄 Recurring"]]}
                  value={isRecurring?"recurring":"once"}
                  onChange={v=>setIsRecurring(v==="recurring")}
                  color={mainColor}
                />
              </div>

              {/* One-time: date only */}
              {!isRecurring && (
                <div>
                  <FL tip={TIPS.date}>Date (optional)</FL>
                  <DateInp value={date} onChange={setDate} placeholder="DD.MM.YYYY"/>
                  <div style={{ fontSize:12, color:T.textSub, marginTop:5 }}>
                    Leave blank to add to {monthName} {yr}.
                  </div>
                </div>
              )}

              {/* Recurring: full schedule */}
              {isRecurring && (
                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

                  {/* Schedule type */}
                  <div>
                    <FL>Schedule Type</FL>
                    <Seg
                      options={[["calendar","📅 Monthly"],["simple","⏱ Fixed interval"]]}
                      value={recType} onChange={setRecType} color={mainColor}
                    />
                  </div>

                  {/* Start / End */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                    <div>
                      <FL tip={TIPS.startDate}>Start Date</FL>
                      <DateInp value={startDate} onChange={setStartDate} placeholder="DD.MM.YYYY"/>
                    </div>
                    <div>
                      <FL tip={TIPS.endDate}>End Date</FL>
                      <DateInp value={endDate} onChange={setEndDate} placeholder="DD.MM.YYYY (opt)"/>
                    </div>
                  </div>

                  {/* Calendar options */}
                  {recType === "calendar" && (
                    <>
                      <div>
                        <FL tip={TIPS.dayOfMonth}>Day of Month</FL>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <TxInput type="number" value={dayOfMonth}
                            onChange={e=>setDayOfMonth(parseInt(e.target.value)||1)}
                            style={{ width:80, fontSize:16 }}/>
                          <span style={{ fontSize:12, color:T.textSub }}>
                            {dayOfMonth>=31?"(last day of month)":dayOfMonth===1?"(1st of month)":""}
                          </span>
                        </div>
                      </div>
                      <div>
                        <FL tip={TIPS.activeMonths}>Active Months</FL>
                        <MonthPicker value={activeMonths}
                          onChange={setActiveMonths} color={mainColor}/>
                      </div>
                      <div>
                        <FL tip={TIPS.activeYears}>Active Years</FL>
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                          {(visibleYears||[]).map(y => {
                            const sel = activeYears.includes(y);
                            return (
                              <button key={y} type="button"
                                onClick={()=>setActiveYears(
                                  sel ? activeYears.filter(x=>x!==y)
                                      : [...activeYears,y].sort()
                                )} style={{
                                  padding:"6px 16px", borderRadius:RADIUS.sm,
                                  fontFamily:FONT, fontSize:13, cursor:"pointer",
                                  fontWeight:sel?700:400,
                                  background:sel?mainColor+"22":"transparent",
                                  color:sel?mainColor:T.textSub,
                                  border:`1px solid ${sel?mainColor:T.border}`,
                                  transition:"all 0.15s",
                                }}>{y}</button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Fixed interval */}
                  {recType === "simple" && (
                    <div>
                      <FL tip={TIPS.intervalDays}>Repeat Every</FL>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <TxInput type="number" value={intervalDays}
                          onChange={e=>setIntervalDays(parseInt(e.target.value)||30)}
                          style={{ width:90, fontSize:16 }}/>
                        <span style={{ color:T.textSub, fontSize:13 }}>days</span>
                        <span style={{ fontSize:11, color:T.textSub }}>
                          ({intervalDays===7?"weekly":intervalDays===14?"fortnightly":
                            intervalDays>=28&&intervalDays<=31?"monthly":
                            intervalDays===90?"quarterly":
                            intervalDays===365?"annual":""})
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Growth rate */}
                  <div style={{ background:T.surface, borderRadius:RADIUS.md,
                    padding:"14px 16px", border:`1px dashed ${T.border}` }}>
                    <div style={{ fontSize:11, fontWeight:700, color:T.textSub,
                      textTransform:"uppercase", letterSpacing:0.5,
                      marginBottom:12, display:"flex", alignItems:"center" }}>
                      Growth Rate (optional) <Tip text={TIPS.growthRate}/>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <div>
                        <FL tip={TIPS.growthRate}>Rate %</FL>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <TxInput type="number" value={growthRate}
                            onChange={e=>setGrowthRate(e.target.value)}
                            placeholder="0" color={T.orange}
                            style={{ fontSize:16 }}/>
                          <span style={{ color:T.textSub }}>%</span>
                        </div>
                      </div>
                      <div>
                        <FL tip={TIPS.growthEvery}>Every N transactions</FL>
                        <TxInput type="number" value={growthEvery}
                          onChange={e=>setGrowthEvery(parseInt(e.target.value)||1)}
                          style={{ fontSize:16 }}/>
                      </div>
                    </div>
                    {growthRate && parseFloat(growthRate) > 0 && parseFloat(amount) > 0 && (
                      <div style={{ fontSize:11, color:T.orange, marginTop:10,
                        background:T.orange+"10", borderRadius:RADIUS.sm,
                        padding:"6px 10px" }}>
                        Preview: {currSym()}{parseFloat(amount).toFixed(2)} →
                        after {growthEvery} payments: {currSym()}{((parseFloat(amount))*(1+parseFloat(growthRate)/100)).toFixed(2)} →
                        after {growthEvery*2} payments: {currSym()}{((parseFloat(amount))*Math.pow(1+parseFloat(growthRate)/100,2)).toFixed(2)}
                      </div>
                    )}
                  </div>

                  <InfoBanner color={T.blue} icon="🔄">
                    This recurring entry will appear automatically in every qualifying month in your <strong>Transactions page</strong> and will affect your <strong>cashflow chart</strong>. Manage all recurring entries from <strong>Recurring &amp; Partners</strong>.
                  </InfoBanner>
                </div>
              )}

              {/* ── Planner ── */}
              <div style={{ background:T.surface,
                border:`1px solid ${addPlanner?T.purple+"60":T.border}`,
                borderRadius:RADIUS.md, padding:"14px 16px",
                transition:"border-color 0.2s" }}>
                <button type="button" onClick={()=>setAddPlanner(p=>!p)} style={{
                  display:"flex", alignItems:"center", gap:10,
                  background:"none", border:"none", cursor:"pointer",
                  fontFamily:FONT, padding:0, width:"100%" }}>
                  <div style={{
                    width:22, height:22, borderRadius:6, flexShrink:0,
                    border:`2px solid ${addPlanner?T.purple:T.border}`,
                    background:addPlanner?T.purple:"transparent",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    transition:"all 0.15s",
                  }}>
                    {addPlanner && <span style={{ color:"#fff", fontSize:12, fontWeight:900 }}>✓</span>}
                  </div>
                  <span style={{ fontSize:13, fontWeight:600,
                    color:addPlanner?T.purple:T.text }}>
                    📌 Also create a Planner decision
                  </span>
                  <Tip text={TIPS.planner}/>
                </button>

                {addPlanner && (
                  <div style={{ marginTop:14 }}>
                    <FL tip={TIPS.plannerCat}>Decision Category</FL>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      {[["cost","💸 Cost"],["investment","📈 Investment"],
                        ["milestone","🏆 Milestone"],["product","📦 Product"]].map(([v,l])=>(
                        <button key={v} type="button" onClick={()=>setPlannerCat(v)} style={{
                          flex:1, padding:"7px 4px", borderRadius:RADIUS.sm,
                          fontFamily:FONT, fontSize:12, cursor:"pointer",
                          fontWeight:plannerCat===v?700:400,
                          background:plannerCat===v?T.purple+"22":"transparent",
                          color:plannerCat===v?T.purple:T.textSub,
                          border:`1px solid ${plannerCat===v?T.purple:T.border}`,
                          transition:"all 0.15s",
                        }}>{l}</button>
                      ))}
                    </div>
                    <div style={{ fontSize:11, color:T.textSub, marginTop:8 }}>
                      A decision "{name||"this transaction"}" will be pinned on your Planner timeline for {monthName} {yr}.
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>

          </div>
    </BottomSheet>
  );
}
